/**
 * Calibration readiness → approval-action state (BIO-309).
 *
 * The BE refuses `Open → PendingApproval` while any contributing
 * instrument was out of calibration during the batch period, and returns the
 * same verdicts from `GET /batches/{id}/calibration-readiness`. These helpers
 * turn those verdicts into the button's disabled state and its explanation, so
 * the UI and the server agree instead of each applying its own rule.
 *
 * Pure, so the decision is unit-testable without rendering the header.
 */

import type { BatchState, CalibrationReadiness, CalibrationReadinessItem } from "../api/types";

/**
 * The only transition calibration gates. A send-back is how an operator goes to
 * fix a calibration, so landing on Open is never gated. Exported so callers can
 * skip fetching readiness entirely outside this edge.
 *
 * Since DataReview was removed this is the FIRST forward hop, not the second —
 * an Open batch now fetches readiness where it previously never did, and its
 * forward button is calibration-blockable.
 */
export const GATED_TARGET: BatchState = "PendingApproval";

/**
 * Verdicts that will actually block `toState`, or `[]` when nothing does.
 *
 * A `resolved` verdict is excluded: the finding was remediated with a
 * calibration record or overridden by an admin, and the BE will not block on it.
 * While the query is still loading (`readiness` undefined) nothing is reported —
 * the server remains the authority, so a pending read must not disable the
 * action on a guess.
 */
export function calibrationBlockers(
  readiness: CalibrationReadiness | undefined,
  toState: BatchState | undefined,
): CalibrationReadinessItem[] {
  if (!readiness || toState !== GATED_TARGET) return [];
  return readiness.items.filter((item) => item.must_resolve && !item.resolved);
}

/**
 * Whether to disable the approval action, from the BE's own `approval_blocked`.
 *
 * Deliberately not `calibrationBlockers(...).length > 0`: when readiness is
 * unavailable (no live config, no active instrument) there are no verdicts to
 * count, yet the transition gate still refuses — a count-based button would sit
 * enabled and disagree with the server.
 */
export function approvalBlocked(
  readiness: CalibrationReadiness | undefined,
  toState: BatchState | undefined,
): boolean {
  if (!readiness || toState !== GATED_TARGET) return false;
  return readiness.approval_blocked;
}

/**
 * Why approval is unavailable, naming the instruments.
 *
 * The BE's own 422 names them for the same reason: a calibration blocker can
 * exist with no issue row to look up, so this text may be the only place an
 * operator learns which instrument to fix.
 */
export function calibrationBlockedReason(
  blockers: CalibrationReadinessItem[],
  unavailableReason?: string | null,
): string {
  if (blockers.length === 0) {
    // Readiness could not be established — the gate refuses with no verdicts to
    // name, so pass the server's own explanation through.
    const detail = unavailableReason ? `: ${unavailableReason}` : "";
    return `Calibration readiness for this period could not be established${detail}.`;
  }
  const tags = blockers.map((b) => b.tag).join(", ");
  const noun = blockers.length === 1 ? "instrument" : "instruments";
  return (
    `${blockers.length} ${noun} without valid calibration covering this period ` +
    `(${tags}). Record a calibration and resolve the finding before sending for approval.`
  );
}
